<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- Header sticky -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
                    <div class="d-flex align-center mb-2">
                        <div class="text-subtitle-2 font-weight-bold primary--text flex-grow-1 text-center">Schemi</div>
                        <v-chip x-small outlined color="primary" class="mr-2">{{ schemiEstesi.length }} schemi</v-chip>
                        <v-btn v-if="canEdit" icon x-small color="primary" @click="apriNuovoSchema">
                            <v-icon>mdi-plus-circle-outline</v-icon>
                        </v-btn>
                    </div>
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Cerca per numero o testo..."
                        outlined rounded dense hide-details clearable
                    />
                </v-card>

                <!-- Skeleton -->
                <template v-if="loading">
                    <v-card v-for="n in 8" :key="n" class="rounded-xl elevation-1 mb-2 px-3 py-2">
                        <v-skeleton-loader type="list-item-two-line" />
                    </v-card>
                </template>

                <!-- Lista schemi -->
                <template v-else>
                    <div v-if="schemiFiltrati.length === 0" class="text-center grey--text py-10">
                        <v-icon large color="grey lighten-1">mdi-book-off-outline</v-icon>
                        <div class="mt-2 text-body-2">Nessuno schema trovato</div>
                    </div>

                    <v-card v-for="item in schemiFiltrati" :key="item.discorso.id"
                            :ref="'schema-card-' + item.discorso.id"
                            class="rounded-xl mb-2 schema-card"
                            :elevation="isSelectedOnTablet(item) ? 0 : 2"
                            :class="{ 'schema-oscurato': item.discorso.isOscurato, 'schema-selected': isSelectedOnTablet(item) }">

                        <!-- Riga principale -->
                        <div class="d-flex align-center px-3 py-2"
                             :class="showExpand ? 'clickable' : ''"
                             @click="clickRow(item)">
                            <!-- Numero badge -->
                            <div class="schema-num-wrap mr-3 flex-shrink-0">
                                <span class="schema-num">{{ item.discorso.diId }}</span>
                            </div>
                            <!-- Tema -->
                            <div class="flex-grow-1" style="min-width:0">
                                <div class="text-body-2 font-weight-medium" style="white-space:normal;line-height:1.35">
                                    {{ item.discorso.tema }}
                                </div>
                                <div class="d-flex align-center mt-1" style="gap:6px">
                                    <span v-if="item.numeroOratori > 0" class="oratori-label">
                                        <v-icon x-small color="primary" class="mr-1">mdi-account-multiple-outline</v-icon>
                                        {{ item.numeroOratori }} orator{{ item.numeroOratori === 1 ? 'e' : 'i' }}
                                    </span>
                                    <span v-else class="oratori-label grey--text">
                                        <v-icon x-small color="grey" class="mr-1">mdi-account-off-outline</v-icon>
                                        Nessun oratore
                                    </span>
                                    <v-chip v-if="item.discorso.isOscurato" x-small color="error" dark>
                                        <v-icon x-small left>mdi-eye-off-outline</v-icon>
                                        Schema attualmente non disponibile
                                    </v-chip>
                                </div>
                            </div>
                            <!-- Azioni -->
                            <div class="d-flex align-center ml-2 flex-shrink-0" style="gap:2px">
                                <v-btn v-if="canEdit"
                                       icon x-small color="grey"
                                       @click.stop="apriSchema(item)">
                                    <v-icon small>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-icon v-if="showExpand"
                                        small color="grey"
                                        style="transition:transform .2s"
                                        :class="{ 'rotate-180': isExpanded(item) }">
                                    mdi-chevron-down
                                </v-icon>
                            </div>
                        </div>

                        <!-- Espansione oratori -->
                        <v-expand-transition>
                            <div v-if="isExpanded(item)">
                                <v-divider />
                                <div v-if="loadingOratori" class="pa-3">
                                    <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-avatar" class="mb-1" />
                                </div>
                                <div v-else-if="oratori.length === 0" class="text-center grey--text py-3 text-caption">
                                    Nessun oratore assegnato
                                </div>
                                <div v-else class="px-3 py-2">
                                    <div v-for="(oratore, i) in oratori" :key="oratore.id"
                                         class="d-flex align-center py-1"
                                         :class="{ 'oratore-divider': i < oratori.length - 1 }">
                                        <v-avatar size="28" color="primary" class="mr-2 flex-shrink-0">
                                            <span class="white--text" style="font-size:0.55rem;font-weight:700">
                                                {{ (oratore.nome ? oratore.nome[0] : '') + (oratore.cognome ? oratore.cognome[0] : '') }}
                                            </span>
                                        </v-avatar>
                                        <div class="flex-grow-1" style="min-width:0">
                                            <div class="d-flex align-center flex-wrap" style="gap:3px">
                                                <span class="text-caption font-weight-bold">{{ oratore.nome }} {{ oratore.cognome }}</span>
                                                <v-chip v-if="oratore.isAnziano"      x-small dark color="teal">A</v-chip>
                                                <v-chip v-if="oratore.isSM"           x-small dark color="blue darken-1">SM</v-chip>
                                                <v-chip v-if="oratore.isAssistente"   x-small dark color="grey darken-2">ASSISTENTE</v-chip>
                                                <v-chip v-if="oratore.isCoordinatore" x-small dark color="deep-orange darken-4">ADM</v-chip>
                                                <v-chip v-if="oratore.isAdmin"        x-small dark color="orange darken-1">APP-ADM</v-chip>
                                            </div>
                                            <div class="text-caption grey--text">{{ oratore.congregazione && oratore.congregazione.nome }}</div>
                                        </div>
                                        <div v-if="oratore.telefono"
                                             class="text-caption grey--text ml-2 flex-shrink-0 d-flex align-center"
                                             style="cursor:pointer"
                                             @click.stop="openTel(oratore.telefono)">
                                            <v-icon x-small color="primary" class="mr-1">mdi-phone-outline</v-icon>{{ oratore.telefono }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-expand-transition>
                    </v-card>
                </template>

            </v-col>
        </v-row>

        <!-- Dialog nuovo schema -->
        <v-dialog v-model="dialogNuovoSchema" max-width="420px" persistent>
            <v-card class="rounded-xl pa-4">
                <div class="text-subtitle-2 font-weight-bold primary--text mb-3">Nuovo schema</div>
                <v-text-field
                    v-model.number="nuovoSchemaDiId"
                    label="Numero schema"
                    type="number"
                    outlined rounded dense hide-details="auto"
                    prepend-inner-icon="mdi-pound"
                    class="mb-3" />
                <v-text-field
                    v-model="nuovoSchemaTema"
                    label="Tema"
                    outlined rounded dense hide-details="auto"
                    prepend-inner-icon="mdi-text-box-outline"
                    class="mb-3" />
                <div v-if="erroreNuovoSchema" class="text-caption error--text mb-2">{{ erroreNuovoSchema }}</div>
                <div class="d-flex justify-end" style="gap:8px">
                    <v-btn text rounded @click="dialogNuovoSchema = false">Annulla</v-btn>
                    <v-btn color="primary" depressed rounded
                        :loading="loadingNuovoSchema"
                        :disabled="!nuovoSchemaDiId || !nuovoSchemaTema"
                        @click="salvaNuovoSchema">
                        Crea
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Snackbars -->
        <v-snackbar v-model="snackSuccess" timeout="2000" color="success" bottom>
            <v-icon dark class="mr-2">mdi-checkbox-marked-circle</v-icon>
            Operazione eseguita con successo.
        </v-snackbar>
        <v-snackbar v-model="snackError" timeout="2000" color="error" bottom>
            <v-icon dark class="mr-2">mdi-alert-circle</v-icon>
            Errore durante l'esecuzione dell'operazione.
        </v-snackbar>
    </v-container>
</template>

<script>
import { callService, callPost, SUPERADMIN_CODE } from '@/modules/utilities.js'
import { VSkeletonLoader } from 'vuetify/lib'

export default {
    components: { VSkeletonLoader },
    inject: {
        tabletState:       { default: null },
        setDetailDiscorso: { default: null },
        setDetailSchema:   { default: null },
    },
    data: () => ({
        schemiEstesi: [],
        oratori: [],
        expanded: [],
        loadingOratori: false,
        loading: false,
        SUPERADMIN_CODE: '',
        search: '',
        showExpand: false,
        canEdit: false,
        snackSuccess: false,
        snackError: false,
        dialogNuovoSchema: false,
        nuovoSchemaDiId: null,
        nuovoSchemaTema: '',
        loadingNuovoSchema: false,
        erroreNuovoSchema: '',
    }),
    computed: {
        isTablet() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        schemiFiltrati() {
            if (!this.search) return this.schemiEstesi;
            const s = this.search.toLowerCase();
            return this.schemiEstesi.filter(item =>
                (item.discorso.diId && String(item.discorso.diId).includes(s)) ||
                (item.discorso.tema && item.discorso.tema.toLowerCase().includes(s))
            );
        }
    },
    methods: {
        openTel(tel) {
            if (tel) this.$openUrl('tel:' + tel.replace(/\s/g, ''));
        },
        isSelectedOnTablet(item) {
            if (!this.isTablet || !this.tabletState || !this.tabletState.detailDiscorso) return false;
            return this.tabletState.detailDiscorso.id === item.discorso.id;
        },
        clickRow(item) {
            if (!this.showExpand) return;
            if (this.isTablet && this.setDetailDiscorso) {
                this.setDetailDiscorso(item.discorso);
            } else {
                this.toggleExpand(item);
            }
        },
        isExpanded(item) {
            return this.expanded.some(e => e.discorso.id === item.discorso.id);
        },
        toggleExpand(item) {
            const idx = this.expanded.findIndex(e => e.discorso.id === item.discorso.id);
            if (idx > -1) {
                this.expanded.splice(idx, 1);
                this.oratori = [];
            } else {
                this.expanded = [item];
                this.expandSchema({ item, value: true });
            }
        },
        async expandSchema({ item, value }) {
            if (value) {
                this.loadingOratori = true;
                const param = [this.$store.state.idCircoscrizione, item.discorso.id];
                const oratidiscorso = await callService("/api/oratore/oratidiscorso", param);
                this.oratori = oratidiscorso.map(od => od.oratore);
                this.loadingOratori = false;
                this.$nextTick(() => {
                    const cardRef = this.$refs['schema-card-' + item.discorso.id];
                    if (cardRef && cardRef[0]) {
                        const el = cardRef[0].$el || cardRef[0];
                        const top = el.getBoundingClientRect().top + window.pageYOffset;
                        window.scrollTo({ top: top - 50, behavior: 'smooth' });
                    }
                });
            } else {
                this.oratori = [];
            }
        },
        apriSchema(schemaEsteso) {
            if (this.isTablet && this.setDetailSchema) {
                this.setDetailSchema(schemaEsteso.discorso.id);
            } else {
                this.$router.push({ name: 'schema', params: { id: schemaEsteso.discorso.id } });
            }
        },
        async caricaSchemi() {
            this.schemiEstesi = await callService("/api/discorso/schemiEstesi", [this.$store.state.idCircoscrizione]);
        },
        apriNuovoSchema() {
            this.nuovoSchemaDiId = null;
            this.nuovoSchemaTema = '';
            this.erroreNuovoSchema = '';
            this.dialogNuovoSchema = true;
        },
        async salvaNuovoSchema() {
            this.erroreNuovoSchema = '';
            this.loadingNuovoSchema = true;
            try {
                const res = await callPost('/api/discorso/creaDiscorso', {
                    partition: this.$store.state.partition,
                    username: this.$store.state.oratore.codice,
                    token: this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    diId: this.nuovoSchemaDiId,
                    tema: this.nuovoSchemaTema,
                });
                if (res) {
                    this.dialogNuovoSchema = false;
                    await this.caricaSchemi();
                    this.snackSuccess = true;
                } else {
                    this.erroreNuovoSchema = 'Numero schema già esistente o errore.';
                }
            } catch {
                this.erroreNuovoSchema = 'Errore durante la creazione.';
            } finally {
                this.loadingNuovoSchema = false;
            }
        },
    },
    watch: {
        '$updaterHub.signals.schemi'() {
            this.caricaSchemi();
        },
    },
    async created() {
        this.loading = true;
        this.SUPERADMIN_CODE = SUPERADMIN_CODE;
        await this.caricaSchemi();
        this.loading = false;
        const o = this.$store.state.oratore;
        this.canEdit = this.$store.state.loginCodice == 250613 || o.codice == SUPERADMIN_CODE;
        this.showExpand = o.codice == SUPERADMIN_CODE ||
            (o._partition == this.$store.state.idCircoscrizione &&
                (o.isAdmin || o.isCoordinatore || o.isAssistente));
        this.$root.$on('schemi:aggiornati', this.caricaSchemi);
    },
    beforeDestroy() {
        this.$root.$off('schemi:aggiornati', this.caricaSchemi);
    },
}
</script>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 4;
}
.schema-selected {
    border: 2px solid var(--v-primary-base) !important;
    background-color: rgba(25, 118, 210, 0.06) !important;
}
.schema-card {
    transition: box-shadow 0.15s;
}
.schema-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
}
.schema-oscurato {
    opacity: 0.75;
}
.schema-num-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    flex-shrink: 0;
}
.schema-num {
    font-size: 0.78rem;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.02em;
}
.theme--dark .schema-num-wrap {
    background: linear-gradient(135deg, #1e88e5 0%, #1976d2 100%);
}
.oratori-label {
    font-size: 0.72rem;
    color: #1976d2;
    font-weight: 600;
    display: flex;
    align-items: center;
}
.clickable {
    cursor: pointer;
}
.clickable:hover {
    background: rgba(0,0,0,0.025);
    border-radius: 10px 10px 0 0;
}
.rotate-180 {
    transform: rotate(180deg);
}
.oratore-divider {
    border-bottom: 1px solid rgba(0,0,0,0.06);
}
.theme--dark .oratore-divider {
    border-bottom: 1px solid rgba(255,255,255,0.06);
}
.field-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0,0,0,0.55);
    margin-bottom: 4px;
}
.theme--dark .field-label {
    color: rgba(255,255,255,0.5);
}
</style>
